import { queryOptions, useQuery } from '@tanstack/react-query';

import { searchServiceKeys } from '@/features/search-service/api/query-key-factory';
import { QueryConfig } from '@/lib/react-query';
import { supabase } from '@/lib/supabase-client';
import { Database, Enums } from '@/lib/types/supabase';
import { logger } from '@/logger';

export type PracticianSearchEntity =
  Database['public']['Functions']['p_global_search']['Returns'][0];
export type PracticianSearchEntitySection = ReturnType<typeof dataTransformer>;
export type PracticianEntityType = Enums<'p_global_search_entity_type'>;

export const getPracticianFinderData = async ({
  query,
}: {
  query?: string;
}) => {
  if (!query) {
    return;
  }

  const { data, error } = await supabase.rpc('p_global_search', {
    query,
  });

  if (error) {
    logger.error('getPracticianFinderData', {
      errorMessage: error,
      query,
    });
    throw error;
  }

  if (!data) return [];

  return data;
};

const dataTransformer = (data: PracticianSearchEntity[] | undefined) => {
  if (!data) return [];

  const sections = data.reduce(
    (acc, entity) => {
      if (!acc[entity.type]) {
        acc[entity.type] = [];
      }

      acc[entity.type].push(entity);

      return acc;
    },
    {} as Record<PracticianEntityType, PracticianSearchEntity[]>,
  );

  return sections;
};

const getPracticianFinderQueryOptions = ({ query }: { query?: string }) => {
  return queryOptions({
    queryKey: searchServiceKeys.finder(query),
    queryFn: () => getPracticianFinderData({ query }),
    enabled: !!query,
  });
};

type UsePracticianFinderOptions = {
  query?: string;
  queryConfig?: QueryConfig<typeof getPracticianFinderData>;
};

export const usePracticianFinder = ({
  query,
  queryConfig,
}: UsePracticianFinderOptions) => {
  return useQuery({
    ...getPracticianFinderQueryOptions({ query }),
    ...queryConfig,
    select: dataTransformer,
  });
};

export const typeToSection = (type: string) => {
  let section = '';

  if (type === 'order.technician') {
    section = 'Commandes (Techniciens)';
  } else if (type === 'service') {
    section = 'Trouvez un service';
  } else if (type === 'technician') {
    section = 'Mes partenaires';
  }

  return section;
};
